import * as React from 'react';

import gql from 'graphql-tag';
import idx from 'idx';
import { flowRight as compose, isNumber } from 'lodash';
import pluralize from 'pluralize';
import { DataProps, graphql, MutateProps, QueryControls } from 'react-apollo';

import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Container from 'components/Container';
// @ts-ignore
import Fieldset from 'components/Fieldset';
import Heading from 'components/Heading';
// @ts-ignore
import Legend from 'components/Legend';
import LoadingIndicator from 'components/LoadingIndicator';
import Menu from 'components/Menu';
import MenuItem from 'components/MenuItem';
// @ts-ignore
import Redirect from 'components/Redirect';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import TitleHeader from 'components/TitleHeader';
// @ts-ignore
import View from 'components/View';
import Avatars from 'lib/Avatars';
// @ts-ignore
import trackView from 'lib/trackView';
// @ts-ignore
import UserActions from 'user/UserActions';

import SetupConfirmClassesListItem from './SetupConfirmClassesListItem';
import SetupRosterConfirmPoll from './SetupRosterConfirmPoll';

import {
  archiveActiveGroups,
  archiveActiveGroupsVariables,
} from '../queries/__generated__/archiveActiveGroups';
import {
  claimAndArchiveProposedGroups,
  claimAndArchiveProposedGroupsVariables,
} from '../queries/__generated__/claimAndArchiveProposedGroups';
import { getConfirmRosteredGroups_me } from '../queries/__generated__/getConfirmRosteredGroups';
import {
  resolveChangesetApprovals,
  resolveChangesetApprovalsVariables,
} from '../queries/__generated__/resolveChangesetApprovals';

import ARCHIVE_ACTIVE_GROUPS_MUTATION from '../queries/ArchiveActiveGroupsMutation.gql';
import CLAIM_AND_ARCHIVE_PROPOSED_GROUPS_MUTATION from '../queries/ClaimAndArchiveProposedGroupsMutation.gql';
import GET_CONFIRM_ROSTERED_GROUPS_QUERY from '../queries/GetConfirmRosteredGroupsQuery.gql';
import RESOLVE_CHANGESET_APPROVALS_MUTATION from '../queries/ResolveChangesetApprovalsMutation.gql';

import './styles/SetupRosterConfirm.scss';

type Avatar = {
  id: number;
  svg_url: string;
};

export type RosterGroupSummary = {
  approvalId?: number;
  id?: number;
  code?: string;
  groupAvatar: Avatar;
  hasRosterSource?: boolean;
  className: string;
  createdAt: string;
  kind: string;
  uuid: string;
};

type Props = {
  proposedGroups: Array<RosterGroupSummary>;
  activeGroups: Array<RosterGroupSummary>;
  suggestedArchivedGroups: Array<RosterGroupSummary>;
  archiveActiveGroups: (classUuids: Array<string>) => Promise<{ data: archiveActiveGroups }>;
  resolveChangesetApprovals: (
    accept: Array<number>,
    reject: Array<number>
  ) => Promise<{ data: resolveChangesetApprovals }>;
  claimAndArchiveClasses: (groupsToArchive?: Array<number> | null) => Promise<string>;
};

type State = {
  proposedClassList?: Array<RosterGroupSummary>;
  archiveClassList?: Array<RosterGroupSummary>;
  statusEndpoint?: string;
  sequentialCallsLoading?: boolean;
  sequentialCallsComplete?: boolean;
};

export class SetupRosterConfirm extends React.Component<Props, State> {
  state: State = {};

  componentWillReceiveProps(nextProps: Props) {
    const { suggestedArchivedGroups, activeGroups, proposedGroups } = nextProps;

    if (suggestedArchivedGroups || activeGroups || proposedGroups) {
      const proposedClassList = this.dedupeActiveGroups(
        activeGroups,
        suggestedArchivedGroups
      ).concat(proposedGroups);
      const archiveClassList = suggestedArchivedGroups;
      this.setState({ proposedClassList, archiveClassList });
    }
  }

  dedupeActiveGroups(
    activeGroups: Array<RosterGroupSummary>,
    suggestedArchivedGroups: Array<RosterGroupSummary>
  ) {
    // if there aren't any groups to archive, nothing to dedupe
    if (!suggestedArchivedGroups) {
      return activeGroups;
    }

    const groupCodesToExclude = suggestedArchivedGroups.map(g => g.code);
    return activeGroups.filter(g => !groupCodesToExclude.includes(g.code));
  }

  getHeader() {
    const { proposedGroups } = this.props;
    const numNewClasses = proposedGroups.length;

    const header =
      numNewClasses > 0
        ? `Confirm your ${numNewClasses} new ${pluralize('class', numNewClasses)}`
        : 'Confirm your classes';

    return header;
  }

  getSubtext() {
    const { proposedGroups, suggestedArchivedGroups } = this.props;
    const numNewClasses = proposedGroups && proposedGroups.length;
    const numArchivedClasses = suggestedArchivedGroups && suggestedArchivedGroups.length;

    let subtext = 'We’ve';

    if (numNewClasses) {
      subtext += ` added ${numNewClasses} new ${pluralize('class', numNewClasses)}`;
      subtext += numArchivedClasses > 0 ? ' and' : '';
    }

    if (numArchivedClasses) {
      subtext += ` archived ${numArchivedClasses} ${pluralize('class', numArchivedClasses)}`;
    }

    subtext +=
      ' based on what we know from your school database. We’ve kept the class codes and the classes you created previously.';

    return subtext;
  }

  renderProposedClassList() {
    const { proposedClassList } = this.state;

    if (!proposedClassList || proposedClassList.length === 0) {
      return (
        <div>
          <Text align="center" element="div" italic className="ConfirmClassesList-noClasses">
            No classes.
          </Text>
        </div>
      );
    }

    return (
      <div>
        {proposedClassList.map((g, index) => {
          const menu = (
            <Menu>
              <MenuItem menuKey={index} onClick={() => this.moveToArchiveList(g)}>
                <TextWithIcon icon="arrow-down-bar" iconSize="medium" iconColor="graphite">
                  Archive class
                </TextWithIcon>
              </MenuItem>
            </Menu>
          );
          return this.renderListItem(g, menu);
        })}
      </div>
    );
  }

  renderArchiveClassList() {
    const { archiveClassList } = this.state;

    if (!archiveClassList || archiveClassList.length === 0) {
      return (
        <div>
          <Text align="center" element="div" italic>
            No classes to be archived.
          </Text>
        </div>
      );
    }

    return (
      <div>
        {archiveClassList.map((g, index) => {
          const menu = (
            <Menu>
              <MenuItem menuKey={index} onClick={() => this.moveToProposedList(g)}>
                <TextWithIcon icon="arrow-up-bar" iconSize="medium" iconColor="graphite">
                  Unarchive class
                </TextWithIcon>
              </MenuItem>
            </Menu>
          );
          return this.renderListItem(g, menu);
        })}
      </div>
    );
  }

  renderListItem(group: RosterGroupSummary, menu: React.ReactElement) {
    return (
      <SetupConfirmClassesListItem
        group={group}
        key={`${group.kind}.${group.id || group.approvalId}`}
        menu={menu}
      />
    );
  }

  moveToProposedList = (group: RosterGroupSummary) => {
    const { proposedClassList, archiveClassList } = this.state;

    if (!archiveClassList || !proposedClassList) {
      throw new Error(
        'at this point archiveClassList  and proposedClassList should be empty or populated arrays'
      );
    }

    // Three different models, two different identifiers
    const identifier = group.id || group.approvalId;
    const updatedArchivedList = archiveClassList.filter(g => identifier !== (g.id || g.approvalId));
    proposedClassList.push(group);

    this.setState({ archiveClassList: updatedArchivedList, proposedClassList });
  };

  moveToArchiveList = (group: RosterGroupSummary) => {
    const { proposedClassList, archiveClassList } = this.state;

    if (!archiveClassList || !proposedClassList) {
      throw new Error(
        'at this point archiveClassList  and proposedClassList should be empty or populated arrays'
      );
    }

    // Three different models, two different identifiers
    const identifier = group.id || group.approvalId;
    const updatedProposedList = proposedClassList.filter(
      g => identifier !== (g.id || g.approvalId)
    );
    archiveClassList.push(group);

    this.setState({ proposedClassList: updatedProposedList, archiveClassList });
  };

  handleFinish = () => {
    const { archiveClassList, proposedClassList } = this.state;
    const {
      archiveActiveGroups,
      resolveChangesetApprovals,
      claimAndArchiveClasses,
      proposedGroups,
    } = this.props;
    const promiseArray: Promise<
      | {
          data: resolveChangesetApprovals | archiveActiveGroups;
        }
      | string
      | void
    >[] = [];
    let statusEndpoint = '';

    if (!archiveClassList || !proposedClassList) {
      throw new Error(
        'at this point archiveClassList  and proposedClassList should be empty or populated arrays'
      );
    }

    const rejectedProposedGroupIds = archiveClassList
      .filter(g => g.kind === 'proposedGroup')
      .map(g => g.id);
    const rejectedApprovalIds = proposedClassList
      .filter(g => g.kind === 'suggestedArchivedGroup')
      .map(g => g.approvalId);
    const acceptedApprovalIds = archiveClassList
      .filter(g => g.kind === 'suggestedArchivedGroup')
      .map(g => g.approvalId);
    const archivedGroupUuids = archiveClassList
      .filter(g => g.kind === 'activeGroup')
      .map(g => g.uuid);

    this.setState({ sequentialCallsLoading: true });

    if (archivedGroupUuids.length !== 0) {
      const promise = archiveActiveGroups(archivedGroupUuids).catch(err => {
        console.warn(err, err && err.stack);
        console.error('failed to handle resolveChangesetApprovals in SetupRosterConfirm');
      });

      promiseArray.push(promise);
    }

    if (rejectedApprovalIds.length + acceptedApprovalIds.length !== 0) {
      const promise = resolveChangesetApprovals(
        acceptedApprovalIds.filter(isNumber),
        rejectedApprovalIds.filter(isNumber)
      ).catch(err => {
        console.warn(err, err && err.stack);
        console.error('failed to handle resolveChangesetApprovals in SetupRosterConfirm');
      });

      promiseArray.push(promise);
    }

    if (proposedGroups.length !== 0) {
      const ids =
        rejectedProposedGroupIds.length === 0 ? null : rejectedProposedGroupIds.filter(isNumber);
      const promise = claimAndArchiveClasses(ids)
        .then(endpoint => {
          statusEndpoint = endpoint;
        })
        .catch(err => {
          console.warn(err, err && err.stack);
          console.error('failed to handle claimAndArchiveClasses in SetupRosterConfirm');
        });

      promiseArray.push(promise);
    }

    Promise.all(promiseArray)
      .then(async () => {
        await UserActions.loadCurrentUser();
        this.setState({ sequentialCallsComplete: true, statusEndpoint });
      })
      .catch((err: Error) => {
        console.warn(err, err && err.stack);
        console.error('failed to handle a promise in handleFinish in SetupRosterConfirm');
      });
  };

  render() {
    const { proposedGroups, activeGroups, suggestedArchivedGroups } = this.props;

    const { statusEndpoint, sequentialCallsLoading, sequentialCallsComplete } = this.state;

    if (statusEndpoint) {
      return <SetupRosterConfirmPoll statusEndpoint={statusEndpoint} />;
    } else if (sequentialCallsComplete) {
      return <Redirect to="/landing" />;
    } else if (!(proposedGroups || activeGroups || suggestedArchivedGroups)) {
      return <LoadingIndicator height={500} align="center" />;
    } else {
      return (
        <div>
          <View className="SetupRosterConfirm" scroll>
            <Container size="medium">
              <TitleHeader title={this.getHeader()} subtext={this.getSubtext()} textAlign="left" />
              <Fieldset>
                <Legend>
                  <Heading level="2" levelStyle="3">
                    Your Class List
                  </Heading>
                </Legend>
                {this.renderProposedClassList()}
              </Fieldset>
              <Fieldset>
                <Legend>
                  <Heading level="2" levelStyle="3">
                    Classes To Be Archived
                  </Heading>
                  <p>
                    These classes are no longer active in your school’s database and are set to be
                    archived. Unarchive them now or you can do this later in Settings, too.
                  </p>
                </Legend>
                {this.renderArchiveClassList()}
              </Fieldset>
            </Container>
          </View>
          <footer className="SetupRosterConfirm-footer">
            <Container>
              <Buttons>
                <Button onClick={this.handleFinish} loading={sequentialCallsLoading}>
                  Finish
                </Button>
              </Buttons>
            </Container>
          </footer>
        </div>
      );
    }
  }
}

const withGroups = graphql(GET_CONFIRM_ROSTERED_GROUPS_QUERY, {
  props: ({ data }) => {
    const { loading, me }: QueryControls & { me: getConfirmRosteredGroups_me } = data as any;
    if (loading || !me) {
      return { loading };
    }

    const proposedGroups = ((me.proposedGroups && me.proposedGroups) || []).map(pg =>
      Object.assign(
        {
          kind: 'proposedGroup',
          hasRosterSource: true,
          groupAvatar: { id: -1, svg_url: Avatars.apple },
        },
        pg
      )
    );
    const activeGroups = me.activeGroups.map(g => Object.assign({ kind: 'activeGroup' }, g));
    const suggestedArchivedGroups = (me.suggestedArchivedGroups || []).map(g => {
      const { code, createdAt, className, hasRosterSource, groupAvatar } = g.group;
      return Object.assign(
        {
          code,
          createdAt,
          className,
          hasRosterSource,
          groupAvatar,
          kind: 'suggestedArchivedGroup',
        },
        g
      );
    });

    return {
      suggestedArchivedGroups,
      activeGroups,
      proposedGroups,
    };
  },
});

type ArchiveActiveGroupsMutation = (prop1: {
  variables: archiveActiveGroupsVariables;
}) => Promise<{ data: archiveActiveGroups }>;

const withArchiveActiveGroups = graphql(ARCHIVE_ACTIVE_GROUPS_MUTATION, {
  props({ mutate }) {
    return {
      archiveActiveGroups(classUuids: Array<string>) {
        // this is significantly easier than trying to use the graphql type generics.
        return ((mutate as any) as ArchiveActiveGroupsMutation)({
          variables: { classUuids },
        }).then(response => {
          if (response.data.archiveClasses.error) {
            throw new Error(response.data.archiveClasses.error.message);
          }
        });
      },
    };
  },
});

type ResolveChangesetApprovalsMutation = (prop1: {
  variables: resolveChangesetApprovalsVariables;
}) => Promise<{ data: resolveChangesetApprovals }>;

const withResolveChangesetApprovals = graphql(RESOLVE_CHANGESET_APPROVALS_MUTATION, {
  props({ mutate }) {
    return {
      resolveChangesetApprovals(accept: Array<number>, reject: Array<number>) {
        return ((mutate as any) as ResolveChangesetApprovalsMutation)({
          variables: { accept, reject },
        }).then(response => {
          if (response.data.resolveChangesetApprovals.error) {
            throw new Error(response.data.resolveChangesetApprovals.error.message);
          }
        });
      },
    };
  },
});

type ClaimAndArchiveProposedGroupsMutation = (prop1: {
  variables: claimAndArchiveProposedGroupsVariables;
}) => Promise<{ data: claimAndArchiveProposedGroups }>;

const withClaimAndArchive = graphql(CLAIM_AND_ARCHIVE_PROPOSED_GROUPS_MUTATION, {
  props({ mutate }) {
    return {
      claimAndArchiveClasses(proposedGroupIdsToArchive: Array<number>) {
        return ((mutate as any) as ClaimAndArchiveProposedGroupsMutation)({
          variables: { proposedGroupIdsToArchive },
        }).then(({ data: { claimAllProposedClasses } }) => claimAllProposedClasses.status_endpoint);
      },
    };
  },
});

export default trackView(
  compose(
    withClaimAndArchive,
    // @ts-ignore not going to try and deal with compose currently in conversion to TS
    withArchiveActiveGroups,
    withResolveChangesetApprovals,
    withGroups
  )(SetupRosterConfirm),
  'sis_roster_confirm'
);
